import { Component, OnInit, Input, Output, EventEmitter, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService, ConfirmationService, LazyLoadEvent } from 'primeng/api';
import { ActivityService } from '../activity.service';
import { ACTIVITY_TYPES } from '../activity-constants';
import { ActivityProviderService } from '@app/_components/activity-provider/activity-provider.service';
import { PictureService } from '@app/_components/picture/picture.service';
import { AlertService, ConstantService } from '@app/_services';
import { SpringQuery } from '@app/_helpers/spring-query';
import { first } from 'rxjs/operators';
import { ActivityFeeService } from '@app/_components/activity-fee/activity-fee.service';
import { ActivityScheduleService } from '@app/_components/activity-schedule/activity-schedule.service';

@Component({
  selector: 'app-activity-form',
  templateUrl: './activity-form.component.html',
  styleUrls: ['./activity-form.component.scss'],
  providers: [MessageService, ConfirmationService],
  standalone: false,
})
export class ActivityFormComponent implements OnInit {

  @Input()  activity: any;
  @Output() doAdd    = new EventEmitter<any>();
  @Output() doUpdate = new EventEmitter<any>();
  @Output() doDelete = new EventEmitter<any>();

  activeTab   = 'details';
  formTitle:  string;
  isNew:      boolean;
  formLoading = false;

  // ── Multi-select provider IDs ──
  selectedProviderIds: any[] = [];

  // ── Days / times / properties ──
  selectedDays:  string[] = ['Mon','Tue','Wed','Thu','Fri','Sat'];
  selectedTimes: string[] = ['8:00 AM','9:00 AM'];
  selectedProps: string[] = [];

  // ── Included / Not Included items ──
  includedItems:    string[] = [];
  notIncludedItems: string[] = [];
  newIncluded    = '';
  newNotIncluded = '';

  // ── Seasonal custom range ──
  seasonalFrom = '';
  seasonalTo   = '';

  // ── Pictures ──
  pictures:          any[] = [];
  uploadedFiles:     any[] = [];
  pictureIndex       = 0;
  isPicture          = false;
  forDeletePictures: any[] = [];
  lazyLoadEvent:     LazyLoadEvent;
  recordTotal        = 0;
  pictureLoading     = false;

  form1:    UntypedFormGroup;
  providers: any[] = [];

  readonly TABS = ['details', 'availability', 'provider', 'properties', 'pictures', 'fees'];

  // ── AI auto-populate ──
  aiPopulateUrl     = '';
  aiPopulateLoading = false;

  // ── Fees & Schedules ──
  fees: any[]      = [];
  feesLoading      = false;
  expandedFeeId: number | null = null;

  // schedules keyed by feeId
  schedulesByFee: { [feeId: number]: any[] } = {};
  schedulesLoading: { [feeId: number]: boolean } = {};

  // inline-add state for fees
  showAddFeeRow    = false;
  feeAddLoading    = false;
  newFee: any      = { name: '', season: '', feeInfant: null, feeYouth: null, feeAdults: null, feeSeniors: null, description: '', status: '0' };

  // inline-add state for schedules
  scheduleAddLoadingByFee: { [feeId: number]: boolean } = {};
  showAddScheduleRowByFee: { [feeId: number]: boolean } = {};
  newScheduleByFee: { [feeId: number]: any } = {};

  // inline-edit state for fees
  editingFeeId: number | null = null;
  editFeeData: any = {};
  feeEditLoading   = false;

  // inline-edit state for schedules
  editingScheduleId: number | null = null;
  editScheduleData: any = {};
  scheduleEditLoading = false;

  get FEE_STATUSES() { return this.activityFeeService.statuses; }
  get SCHEDULE_STATUSES() { return this.activityScheduleService.statuses; }

  get CATEGORIES()    { return this.activityService.categories; }
  get PROPERTIES()    { return this.activityService.properties; }
  get LEAD_TIMES()    { return this.activityService.leadTimes; }
  get SEASONAL_OPTS() { return this.activityService.seasonalOptions; }
  get TIME_SLOTS()    { return this.activityService.timeSlots; }
  get LOCATIONS()     { return this.constantService.locations; }

  readonly DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  readonly ACTIVITY_TYPES = ACTIVITY_TYPES;

  get isCustomSeasonal(): boolean {
    return this.form1?.get('seasonal')?.value === 'Custom date range';
  }

  get activityId(): number | null {
    return this.form1?.get('activityId')?.value || null;
  }

  constructor(
    private fb:                      UntypedFormBuilder,
    private activityService:         ActivityService,
    private activityProviderService: ActivityProviderService,
    private pictureService:          PictureService,
    private alertService:            AlertService,
    private messageService:          MessageService,
    private confirmationService:     ConfirmationService,
    public  constantService:         ConstantService,
    private activityFeeService:      ActivityFeeService,
    private activityScheduleService: ActivityScheduleService,
    @Inject(MAT_DIALOG_DATA) data: any,
  ) {
    this.formTitle = data.title;
    this.activity  = data.activity;
  }

  ngOnInit(): void {
    this.isNew = !this.activity?.activityId;
    this.buildForm();

    if (this.activity) {
      this.form1.patchValue(this.activity);

      if (this.activity.availableDays) {
        this.selectedDays = this.activity.availableDays.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      if (this.activity.startTimes) {
        this.selectedTimes = this.activity.startTimes.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      if (this.activity.properties) {
        this.selectedProps = this.activity.properties.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      if (this.activity.activityProviders?.length) {
        this.selectedProviderIds = this.activity.activityProviders.map((p: any) => p.activityProviderId);
        this.providers = [...this.activity.activityProviders];
      } else if (this.activity.activityProviderId) {
        this.selectedProviderIds = String(this.activity.activityProviderId)
          .split(',').map((s: string) => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
      }
      if (this.activity.included) {
        this.includedItems = this.activity.included.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      if (this.activity.notIncluded) {
        this.notIncludedItems = this.activity.notIncluded.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      if (this.activity.seasonalCustomRange) {
        const parts = this.activity.seasonalCustomRange.split(' to ');
        if (parts.length === 2) {
          this.seasonalFrom = parts[0].trim();
          this.seasonalTo   = parts[1].trim();
        }
      }
    }

    this.loadProviders();
    this.activityService.loadProperties();

    if (!this.isNew && this.activityId) {
      this.loadPictures();
      this.loadFees();
    }
  }

  buildForm() {
    this.form1 = this.fb.group({
      activityId:          [this.activity?.activityId],
      name:                [''],
      category:            [''],
      activityType:        [''],
      status:              ['draft'],
      invoiceType:         [''],
      shortDesc:           [''],
      fullDesc:            [''],
      overviewDesc:        [''],
      detailsDesc:         [''],
      highlightDesc:       [''],
      feeDesc:             [''],
      scheduleDesc:        [''],
      termsDesc:           [''],
      leadDescription:     [''],
      opsNotes:            [''],
      rate:                [''],
      minGuests:           [''],
      maxGuests:           [''],
      duration:            [''],
      meetingLocation:     [''],
      leadTime:            ['No minimum'],
      highlights:          [''],   // TinyMCE rich-text
      tags:                [''],
      ageRestriction:      [''],
      physicalRequirement: [''],
      internalNote:        [''],
      included:            [''],
      notIncluded:         [''],
      seasonal:            ['Year-round'],
      seasonalCustomRange: [''],
      blackoutDates:       [''],
      availabilityNote:    [''],
      pickUpAvailable:     [''],
      accessibleTransport: [''],
      eatingTime:          [''],
      language:            [''],
      activityProviderId:  [''],
      locationId:          [''],
      featured:            [false],
      priority:            [''],
      adrType: [''], adrStreetNumber:[''], adrStreetName:[''], adrUnit:[''], adr2:[''],
      adrCity:[''], adrCounty:[''], adrCountry:[''], adrRegion:[''], adrState:[''],
      adrPostalCode:[''], adrIsland:[''], adrCrossStreet:[''], adrLongitude:[''], adrLatitude:[''],
      locationCategory:    [''],
    });
  }

  loadProviders() {
    this.activityProviderService.findAll().pipe(first()).subscribe(
      res  => { this.providers = res.data.activityProviders || []; },
      err  => this.alertService.error(err)
    );
  }

  // ── Day / Time / Property toggles ──
  toggleDay(day: string)   { this.selectedDays  = this.selectedDays.includes(day)   ? this.selectedDays.filter(d => d !== day)   : [...this.selectedDays, day]; }
  toggleTime(time: string) { this.selectedTimes = this.selectedTimes.includes(time) ? this.selectedTimes.filter(t => t !== time) : [...this.selectedTimes, time]; }
  toggleProp(prop: string) { this.selectedProps = this.selectedProps.includes(prop) ? this.selectedProps.filter(p => p !== prop) : [...this.selectedProps, prop]; }
  selectAllDays()          { this.selectedDays  = [...this.DAYS]; }

  // ── Seasonal custom range ──
  onSeasonalChange() {
    if (!this.isCustomSeasonal) {
      this.seasonalFrom = '';
      this.seasonalTo   = '';
      this.form1.get('seasonalCustomRange')?.setValue('');
    }
  }
  updateSeasonalRange() {
    if (this.seasonalFrom && this.seasonalTo) {
      this.form1.get('seasonalCustomRange')?.setValue(`${this.seasonalFrom} to ${this.seasonalTo}`);
    }
  }

  // ── Included items ──
  addIncluded() {
    const v = this.newIncluded.trim();
    if (!v) return;
    this.includedItems.push(v);
    this.newIncluded = '';
    this.form1.get('included')?.setValue(this.includedItems.join(', '));
  }
  removeIncluded(i: number) {
    this.includedItems.splice(i, 1);
    this.form1.get('included')?.setValue(this.includedItems.join(', '));
  }
  onIncludedKey(e: KeyboardEvent) { if (e.key === 'Enter') { e.preventDefault(); this.addIncluded(); } }

  // ── Not Included items ──
  addNotIncluded() {
    const v = this.newNotIncluded.trim();
    if (!v) return;
    this.notIncludedItems.push(v);
    this.newNotIncluded = '';
    this.form1.get('notIncluded')?.setValue(this.notIncludedItems.join(', '));
  }
  removeNotIncluded(i: number) {
    this.notIncludedItems.splice(i, 1);
    this.form1.get('notIncluded')?.setValue(this.notIncludedItems.join(', '));
  }
  onNotIncludedKey(e: KeyboardEvent) { if (e.key === 'Enter') { e.preventDefault(); this.addNotIncluded(); } }

  // ── Pictures ──
  loadPictures(event?: LazyLoadEvent) {
    if (event) this.lazyLoadEvent = event;
    if (!this.activityId) return;
    this.pictureLoading = true;
    const sq = new SpringQuery();
    sq.addEquals('activityVO.activityId', String(this.activityId));
    this.pictureService.findByFilter(0, 200, null, null, [], null, {}, sq)
      .pipe(first()).subscribe(
        (res: any) => {
          this.pictures       = res.data.pictures || [];
          this.recordTotal    = res.page?.recordTotal || this.pictures.length;
          this.isPicture      = this.pictures.length === 0;
          this.pictureLoading = false;
        },
        (err: any) => {
          this.pictureLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message });
        }
      );
  }

  myUploader(event: any) {
    this.pictureIndex  = 0;
    this.uploadedFiles = event.files;
    this.uploadPicture();
  }

  uploadPicture() {
    this.pictureLoading = true;
    try {
      this.pictureService.uploadFileActivity(this.uploadedFiles[this.pictureIndex], this.activityId)
        .subscribe(
          (_res: any) => {
            this.pictureIndex++;
            if (this.uploadedFiles.length !== this.pictureIndex) {
              this.uploadPicture();
            } else {
              this.loadPictures();
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'All images uploaded.' });
              this.pictureLoading = false;
            }
          },
          (err: any) => {
            this.pictureIndex++;
            if (this.uploadedFiles.length !== this.pictureIndex) {
              this.uploadPicture();
            } else { this.pictureLoading = false; }
            this.messageService.add({ severity: 'error', summary: 'Upload Error', detail: err.message });
          }
        );
    } catch (e: any) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: e.message });
    }
  }

  onDeletePicture(pic: any) {
    this.confirmationService.confirm({
      message: `Delete picture #${pic.pictureId}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.pictureService.removeBlogPicture(pic.pictureId).pipe(first()).subscribe(
          (_res: any) => {
            this.pictures          = this.pictures.filter(p => p.pictureId !== pic.pictureId);
            this.forDeletePictures = [];
            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Picture removed.' });
          },
          (err: any) => this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message })
        );
      }
    });
  }

  deleteCheckedPictures() {
    if (!this.forDeletePictures.length) return;
    this.pictureService.removeMultipleBlogPicture(this.forDeletePictures).pipe(first()).subscribe(
      (_res: any) => {
        this.pictures          = this.pictures.filter(p => !this.forDeletePictures.includes(p.pictureId));
        this.forDeletePictures = [];
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Selected pictures removed.' });
      },
      (err: any) => this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message })
    );
  }

  togglePictureCheck(pic: any) {
    const id = pic.pictureId;
    if (this.forDeletePictures.includes(id)) {
      this.forDeletePictures = this.forDeletePictures.filter(x => x !== id);
    } else {
      this.forDeletePictures.push(id);
    }
  }

  generateUrl(url: string): string {
    return url ? url.replace(/img/gi, 's3.us-east-1.amazonaws.com/img') : '';
  }

  getCategoryInfo(cat: string) {
    const c = this.CATEGORIES.find((x: any) => x.value === cat);
    return c || { label: cat, cssClass: '' };
  }

  saveDraft() { this.save('draft'); }

  savePublish() {
    const nameVal = (this.form1.get('name')?.value || '').trim();
    if (nameVal.length < 3) {
      this.form1.get('name')?.setErrors({ required: true });
      this.form1.get('name')?.markAsTouched();
      this.activeTab = 'details';
      this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Activity name is required (at least 3 characters).' });
      return;
    }
    this.save('active');
  }

  private save(status: string) {
    if (this.isCustomSeasonal) { this.updateSeasonalRange(); }

    const raw = this.form1.value;
    const payload = {
      ...raw,
      status,
      published:          status === 'active',
      availableDays:      this.selectedDays.join(', '),
      startTimes:         this.selectedTimes.join(', '),
      properties:         this.selectedProps.join(', '),
      activityProviderId: this.selectedProviderIds.join(', '),
      included:           this.includedItems.join(', '),
      notIncluded:        this.notIncludedItems.join(', '),
      // Integer fields: coerce empty strings to null so Jackson can deserialize them
      ...this.coerceIntegerFields(raw),
    };

    this.formLoading = true;
    const obs = this.isNew
      ? this.activityService.add(payload)
      : this.activityService.update(payload);

    obs.pipe(first()).subscribe(
      (res: any) => {
        if (!res) {
          this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'No response received from server.' });
          this.formLoading = false; return;
        }
        const saved = res.data?.activity;
        if (!saved) {
          this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: res?.message || 'Server did not return saved activity data.' });
          this.formLoading = false; return;
        }
        if (this.isNew) {
          this.isNew = false;
          this.form1.get('activityId')?.setValue(saved.activityId);
          this.loadPictures();
          this.loadFees();
          this.doAdd.emit({ ...saved, _savedStatus: status });
        } else {
          this.doUpdate.emit({ ...saved, _savedStatus: status });
        }
        this.formLoading = false;
      },
      (err: any) => {
        // ErrorInterceptor transforms HttpErrorResponse into a plain string (err.error.message).
        // Handle both the transformed string case and the raw HttpErrorResponse case.
        const detail = typeof err === 'string'
          ? err
          : (err?.error?.message || err?.error?.error || err?.statusText || err?.message || 'An unexpected error occurred. Check the browser console for details.');
        this.messageService.add({ severity: 'error', summary: 'Save Failed', detail });
        this.formLoading = false;
      }
    );
  }

  private coerceIntegerFields(raw: any): { minGuests: number | null; maxGuests: number | null; priority: number | null; locationId: number | null } {
    const toInt = (v: any) => (v === '' || v === null || v === undefined) ? null : Number(v);
    return {
      minGuests:  toInt(raw.minGuests),
      maxGuests:  toInt(raw.maxGuests),
      priority:   toInt(raw.priority),
      locationId: toInt(raw.locationId),
    };
  }

  onArchive() {
    const raw = this.form1.value;
    const payload = { ...raw, status: 'archived', ...this.coerceIntegerFields(raw) };
    this.activityService.update(payload).pipe(first()).subscribe(
      (res: any) => this.doDelete.emit(res.data.activity),
      (err: any) => this.alertService.error(err)
    );
  }

  runAiPopulate() {
    const id = this.activityId;
    if (!id) {
      this.messageService.add({ severity: 'warn', summary: 'Save First', detail: 'Save the activity before using AI auto-fill.' });
      return;
    }
    const url = this.aiPopulateUrl.trim();
    if (!url) {
      this.messageService.add({ severity: 'warn', summary: 'URL Required', detail: 'Enter the provider website URL.' });
      return;
    }
    this.aiPopulateLoading = true;
    this.activityService.aiPopulate(id, url).pipe(first()).subscribe(
      (res: any) => {
        this.aiPopulateLoading = false;
        const saved = res.data?.activity;
        if (!saved) {
          this.messageService.add({ severity: 'error', summary: 'AI Error', detail: res?.message || 'AI did not return activity data.' });
          return;
        }
        // Patch the form with returned fields
        this.form1.patchValue({
          name:          saved.name          || this.form1.get('name')?.value,
          category:      saved.category      || this.form1.get('category')?.value,
          shortDesc:     saved.shortDesc     || this.form1.get('shortDesc')?.value,
          highlightDesc: saved.highlightDesc || this.form1.get('highlightDesc')?.value,
          duration:      saved.duration      || this.form1.get('duration')?.value,
          ageRestriction:saved.ageRestriction|| this.form1.get('ageRestriction')?.value,
        });
        // Patch included / notIncluded chip lists
        if (saved.included) {
          this.includedItems = saved.included.split(',').map((s: string) => s.trim()).filter(Boolean);
          this.form1.get('included')?.setValue(this.includedItems.join(', '));
        }
        if (saved.notIncluded) {
          this.notIncludedItems = saved.notIncluded.split(',').map((s: string) => s.trim()).filter(Boolean);
          this.form1.get('notIncluded')?.setValue(this.notIncludedItems.join(', '));
        }
        this.messageService.add({ severity: 'success', summary: 'AI Auto-fill Complete', detail: 'Fields updated from provider website.' });
      },
      (err: any) => {
        this.aiPopulateLoading = false;
        const detail = err?.error?.message || err?.message || 'AI auto-fill failed.';
        this.messageService.add({ severity: 'error', summary: 'AI Error', detail });
      }
    );
  }

  // ── Fees & Schedules helpers ──

  emptyFee(): any {
    return { name: '', season: '', feeInfant: null, feeYouth: null, feeAdults: null, feeSeniors: null, description: '', status: '0' };
  }

  emptySchedule(): any {
    return { name: '', season: '', startTime: '', endTime: '', startTimeDesc: '', endTimeDesc: '', duration: null, description: '', status: '0' };
  }

  loadFees() {
    if (!this.activityId) return;
    this.feesLoading = true;
    this.activityFeeService.findByActivityId(this.activityId).pipe(first()).subscribe(
      (res: any) => {
        this.fees = res.data?.activityFees || [];
        this.feesLoading = false;
      },
      (err: any) => {
        this.feesLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load fees: ' + (err.message || err) });
      }
    );
  }

  toggleFeeExpand(fee: any) {
    if (this.expandedFeeId === fee.activityFeeId) {
      this.expandedFeeId = null;
    } else {
      this.expandedFeeId = fee.activityFeeId;
      if (!this.schedulesByFee[fee.activityFeeId]) {
        this.loadSchedules(fee.activityFeeId);
      }
    }
  }

  loadSchedules(feeId: number) {
    this.schedulesLoading[feeId] = true;
    this.activityScheduleService.findByActivityFeeId(feeId).pipe(first()).subscribe(
      (res: any) => {
        this.schedulesByFee[feeId] = res.data?.activitySchedules || [];
        this.schedulesLoading[feeId] = false;
      },
      (err: any) => {
        this.schedulesLoading[feeId] = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load schedules: ' + (err.message || err) });
      }
    );
  }

  // ── Fee add ──

  startAddFee() {
    this.newFee = this.emptyFee();
    this.showAddFeeRow = true;
  }

  cancelAddFee() {
    this.showAddFeeRow = false;
    this.newFee = this.emptyFee();
  }

  hasFeePrice(fee: any): boolean {
    return !!(fee.feeInfant || fee.feeYouth || fee.feeAdults || fee.feeSeniors);
  }

  saveNewFee() {
    if (!this.newFee.name?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Fee name is required.' });
      return;
    }
    if (!this.hasFeePrice(this.newFee)) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'At least one price tier (Infant/Youth/Adult/Senior) must be filled.' });
    }
    const payload = { ...this.newFee, activityId: this.activityId };
    this.feeAddLoading = true;
    this.activityFeeService.add(payload).pipe(first()).subscribe(
      (res: any) => {
        const saved = res.data?.activityFee;
        if (saved) { this.fees = [saved, ...this.fees]; }
        this.showAddFeeRow = false;
        this.newFee = this.emptyFee();
        this.feeAddLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Fee Added', detail: saved?.name || 'Fee saved.' });
      },
      (err: any) => {
        this.feeAddLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not save fee: ' + (err.message || err) });
      }
    );
  }

  // ── Fee edit ──

  startEditFee(fee: any) {
    this.editingFeeId = fee.activityFeeId;
    this.editFeeData = { ...fee };
  }

  cancelEditFee() {
    this.editingFeeId = null;
    this.editFeeData = {};
  }

  saveEditFee() {
    if (!this.editFeeData.name?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Fee name is required.' });
      return;
    }
    this.feeEditLoading = true;
    this.activityFeeService.update(this.editFeeData).pipe(first()).subscribe(
      (res: any) => {
        const saved = res.data?.activityFee;
        if (saved) {
          const idx = this.fees.findIndex(f => f.activityFeeId === saved.activityFeeId);
          if (idx >= 0) { this.fees[idx] = saved; this.fees = [...this.fees]; }
        }
        this.editingFeeId = null;
        this.editFeeData = {};
        this.feeEditLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Fee Updated', detail: saved?.name || 'Fee updated.' });
      },
      (err: any) => {
        this.feeEditLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not update fee: ' + (err.message || err) });
      }
    );
  }

  // ── Fee delete ──

  deleteFee(fee: any) {
    this.confirmationService.confirm({
      message: `Delete fee "${fee.name}"? All its schedules will also be removed.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.activityFeeService.delete(fee.activityFeeId).pipe(first()).subscribe(
          (_res: any) => {
            this.fees = this.fees.filter(f => f.activityFeeId !== fee.activityFeeId);
            if (this.expandedFeeId === fee.activityFeeId) { this.expandedFeeId = null; }
            delete this.schedulesByFee[fee.activityFeeId];
            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `Fee "${fee.name}" deleted.` });
          },
          (err: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not delete fee: ' + (err.message || err) });
          }
        );
      }
    });
  }

  // ── Schedule add ──

  startAddSchedule(feeId: number) {
    this.showAddScheduleRowByFee[feeId] = true;
    this.newScheduleByFee[feeId] = this.emptySchedule();
  }

  cancelAddSchedule(feeId: number) {
    this.showAddScheduleRowByFee[feeId] = false;
    delete this.newScheduleByFee[feeId];
  }

  saveNewSchedule(feeId: number) {
    const s = this.newScheduleByFee[feeId];
    if (!s?.name?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Schedule name is required.' });
      return;
    }
    if (!s.startTime) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Start time is required.' });
      return;
    }
    const payload = { ...s, activityFeeId: feeId };
    this.scheduleAddLoadingByFee[feeId] = true;
    this.activityScheduleService.add(payload).pipe(first()).subscribe(
      (res: any) => {
        const saved = res.data?.activitySchedule;
        if (saved) {
          const list = this.schedulesByFee[feeId] || [];
          this.schedulesByFee[feeId] = [saved, ...list];
        }
        this.showAddScheduleRowByFee[feeId] = false;
        delete this.newScheduleByFee[feeId];
        this.scheduleAddLoadingByFee[feeId] = false;
        this.messageService.add({ severity: 'success', summary: 'Schedule Added', detail: saved?.name || 'Schedule saved.' });
      },
      (err: any) => {
        this.scheduleAddLoadingByFee[feeId] = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not save schedule: ' + (err.message || err) });
      }
    );
  }

  // ── Schedule edit ──

  startEditSchedule(schedule: any) {
    this.editingScheduleId = schedule.activityScheduleId;
    this.editScheduleData = { ...schedule };
  }

  cancelEditSchedule() {
    this.editingScheduleId = null;
    this.editScheduleData = {};
  }

  saveEditSchedule(feeId: number) {
    if (!this.editScheduleData.name?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Schedule name is required.' });
      return;
    }
    this.scheduleEditLoading = true;
    this.activityScheduleService.update(this.editScheduleData).pipe(first()).subscribe(
      (res: any) => {
        const saved = res.data?.activitySchedule;
        if (saved) {
          const list = this.schedulesByFee[feeId] || [];
          const idx = list.findIndex((s: any) => s.activityScheduleId === saved.activityScheduleId);
          if (idx >= 0) { list[idx] = saved; this.schedulesByFee[feeId] = [...list]; }
        }
        this.editingScheduleId = null;
        this.editScheduleData = {};
        this.scheduleEditLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Schedule Updated', detail: saved?.name || 'Schedule updated.' });
      },
      (err: any) => {
        this.scheduleEditLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not update schedule: ' + (err.message || err) });
      }
    );
  }

  // ── Schedule delete ──

  deleteSchedule(feeId: number, schedule: any) {
    this.confirmationService.confirm({
      message: `Delete schedule "${schedule.name}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.activityScheduleService.delete(schedule.activityScheduleId).pipe(first()).subscribe(
          (_res: any) => {
            const list = this.schedulesByFee[feeId] || [];
            this.schedulesByFee[feeId] = list.filter((s: any) => s.activityScheduleId !== schedule.activityScheduleId);
            this.messageService.add({ severity: 'success', summary: 'Deleted', detail: `Schedule "${schedule.name}" deleted.` });
          },
          (err: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not delete schedule: ' + (err.message || err) });
          }
        );
      }
    });
  }

  errMsg(field: string): string | null {
    const c = this.form1.get(field);
    if (!c || !c.touched || c.valid) return null;
    if (c.hasError('required'))  return 'Required';
    if (c.hasError('minlength')) return 'Too short';
    return 'Invalid';
  }
}
